<script setup lang="ts">
import { nextTick, onBeforeUnmount, watch } from 'vue'
import VuePdfEmbed from 'vue-pdf-embed'
import {
  $acceptWaitingRoomFile,
  $getProjectFileControl,
  $getS3FileDownloadUrls,
  $deleteProjectFileVersion,
  $makeMainProjectFileVersion,
  WAITING_ROOM_ARCHIVE_APPROVE_TIMEOUT_MS,
  type ProjectFileControlPayload,
  type ProjectFileControlVersion,
  type ProjectFileControlWaitingRoomDuplicateContext,
  type WaitingRoomFile,
} from '@/api/fileManagement'
import ProjectFilesWaitingRoomDuplicateComparisonDialog from '@/components/projects/file-management/ProjectFilesWaitingRoomDuplicateComparisonDialog.vue'
import { downloadFreshFiles } from '@/helpers/utils/downloadAllFilesUsingLinks'
import { enableTableDragScroll } from '@/helpers/global-items/enableTableDragScroll'
import { useSnackbarStore } from '@/store/snackbar'
import { formatDateTimeCanada24 } from '@/helpers/utils/dateFormatterHelper'
import { can } from '@layouts/plugins/casl'
import ability from '@/plugins/casl/ability'

type DuplicateComparisonConfirmPayload = {
  duplicateResolution: 'waiting_room_file' | 'current_file'
  waitingRoomFileId: string
  rejectWaitingRoomFileIds: string[]
}

interface Props {
  modelValue: boolean
  projectId: number | string | string[] | null | undefined
  fileKey: string | null
}

const props = defineProps<Props>()

interface Emit {
  (e: 'update:modelValue', value: boolean): void
}

const emit = defineEmits<Emit>()

const snackbarStore = useSnackbarStore()

const loading = ref(false)
const payload = ref<ProjectFileControlPayload | null>(null)
let cleanupTableDragScroll: (() => void) | null = null

const loadingPreview = ref<Set<string>>(new Set())
const loadingDownload = ref<Set<string>>(new Set())
const loadingActions = ref<Set<string>>(new Set())
const selectedHistoricalIds = ref<string[]>([])
const linkedItemsAccordionModel = ref<string[]>([])

const previewUrl = ref<string | null>(null)
const previewFilename = ref('')
const previewKey = ref<string | null>(null)
const isPreviewLoading = computed(() => loadingPreview.value.size > 0)
const selectedHistoricalVersions = computed(() => {
  if (!payload.value) return []

  const selectedIds = new Set(selectedHistoricalIds.value)
  return payload.value.historical_files.filter(version => selectedIds.has(version.id))
})
const allHistoricalSelected = computed(() => {
  const historicalFiles = payload.value?.historical_files ?? []
  return historicalFiles.length > 0 && selectedHistoricalVersions.value.length === historicalFiles.length
})
const isHistoricalSelectionIndeterminate = computed(() => (
  selectedHistoricalVersions.value.length > 0 && !allHistoricalSelected.value
))
const isDownloadingSelectedHistoricalVersions = computed(() => (
  selectedHistoricalVersions.value.some(version => loadingDownload.value.has(version.key))
))

const showConfirmDialog = ref(false)
const confirmVersion = ref<ProjectFileControlVersion | null>(null)
const confirmAction = ref<'make-main' | 'delete'>('make-main')
const waitingRoomDuplicateFile = ref<WaitingRoomFile | null>(null)
const waitingRoomDuplicateContext = ref<ProjectFileControlWaitingRoomDuplicateContext | null>(null)
const showWaitingRoomDuplicatePrompt = ref(false)
const showWaitingRoomDuplicateComparison = ref(false)
const waitingRoomDuplicateApprovalLoading = ref(false)

const selectedItemInPreview = computed(() => {
  if (!payload.value || !previewKey.value) {
    return null
  }

  const allVersions = [
    payload.value.main_version,
    ...payload.value.historical_files,
  ].filter(Boolean) as ProjectFileControlVersion[]

  return allVersions.find(v => v.key === previewKey.value) ?? null
})

const resolvedProjectId = computed<number | string | null>(() => {
  if (Array.isArray(props.projectId)) {
    return props.projectId[0] ?? null
  }

  return props.projectId ?? null
})

const isImageFile = (filename: string) => {
  return /\.(png|jpe?g|heic|heif|svg|gif|webp|bmp)$/i.test(filename)
}

const isPdfFile = (filename: string) => {
  return /\.(pdf|ai)$/i.test(filename)
}

const isTxtFile = (filename: string) => {
  return /\.txt$/i.test(filename)
}

const formatDate = (value?: string) => formatDateTimeCanada24(value)

const formatAuditUser = (value?: string | null) => {
  const trimmed = (value || '').trim()
  return trimmed || 'System'
}

const syncTableDragScroll = async () => {
  cleanupTableDragScroll?.()
  cleanupTableDragScroll = null

  if (!payload.value) {
    return
  }

  await nextTick()
  cleanupTableDragScroll = enableTableDragScroll('file-control-table-wrapper')
}

watch(payload, () => {
  void syncTableDragScroll()
})

onBeforeUnmount(() => {
  cleanupTableDragScroll?.()
  cleanupTableDragScroll = null
})

const isMakeMainLoading = (fileId: string) => {
  return loadingActions.value.has(`make-main:${fileId}`)
}

const isDeleteLoading = (fileId: string) => loadingActions.value.has(`delete:${fileId}`)

const isDownloadLoading = (fileKey: string) => loadingDownload.value.has(fileKey)

const canMakeMain = () => {
  return ability.can('make_main', 'fmfilecontrol')
}

const canArchive = () => ability.can('archive', 'fmfilecontrol')

const setActionLoading = (action: 'make-main' | 'delete', fileId: string, isLoading: boolean) => {
  const key = `${action}:${fileId}`
  const next = new Set(loadingActions.value)

  if (isLoading) {
    next.add(key)
  }
  else {
    next.delete(key)
  }

  loadingActions.value = next
}

const closeDialog = () => {
  emit('update:modelValue', false)
}

const closePreview = () => {
  previewUrl.value = null
  previewFilename.value = ''
  previewKey.value = null
}

const resetState = () => {
  payload.value = null
  loadingPreview.value = new Set()
  loadingDownload.value = new Set()
  loadingActions.value = new Set()
  selectedHistoricalIds.value = []
  linkedItemsAccordionModel.value = []
  showConfirmDialog.value = false
  confirmVersion.value = null
  confirmAction.value = 'make-main'
  waitingRoomDuplicateFile.value = null
  showWaitingRoomDuplicatePrompt.value = false
  showWaitingRoomDuplicateComparison.value = false
  waitingRoomDuplicateApprovalLoading.value = false
  closePreview()
}

const applyControlPayload = (nextPayload: ProjectFileControlPayload) => {
  payload.value = nextPayload
  selectedHistoricalIds.value = []

  const visibleKeys = new Set<string>()
  if (nextPayload.main_version?.key) {
    visibleKeys.add(nextPayload.main_version.key)
    if (previewKey.value === null) {
      previewVersion(nextPayload.main_version)
    }
  }
  nextPayload.historical_files.forEach(item => visibleKeys.add(item.key))

  if (previewKey.value && !visibleKeys.has(previewKey.value)) {
    closePreview()
  }
}

const fetchControlPayload = async () => {
  if (!resolvedProjectId.value || !props.fileKey) {
    payload.value = null
    return
  }

  loading.value = true
  try {
    const response = await $getProjectFileControl(resolvedProjectId.value, {
      key: props.fileKey,
    })
    applyControlPayload(response.data)
  } catch (error: any) {
    payload.value = null
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Control Failed',
      text: error?.response?.data?.detail || error?.message || 'Unable to load file control details.',
    })
  } finally {
    loading.value = false
  }
}

const previewVersion = async (version: ProjectFileControlVersion) => {
  if (!resolvedProjectId.value) return

  loadingPreview.value = new Set([...loadingPreview.value, version.key])
  try {
    const data = await $getS3FileDownloadUrls(resolvedProjectId.value, {
      key: version.key,
      intent: 'preview',
    })
    const url = data?.url ?? data?.download_url
    if (!url) throw new Error('No preview URL returned')

    previewKey.value = version.key
    previewFilename.value = version.filename
    previewUrl.value = url
  } catch (error: any) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Preview Failed',
      text: error?.response?.data?.detail || error?.message || 'Unable to preview file version.',
    })
  } finally {
    const next = new Set(loadingPreview.value)
    next.delete(version.key)
    loadingPreview.value = next
  }
}

const getFreshDownloadTarget = (version: ProjectFileControlVersion) => ({
  projectId: resolvedProjectId.value!,
  fileId: version.id,
  filename: version.filename,
})

const downloadVersions = async (versions: ProjectFileControlVersion[]) => {
  if (!resolvedProjectId.value || !versions.length) return null

  loadingDownload.value = new Set([
    ...loadingDownload.value,
    ...versions.map(version => version.key),
  ])
  try {
    return await downloadFreshFiles(versions.map(getFreshDownloadTarget))
  } finally {
    const next = new Set(loadingDownload.value)
    versions.forEach(version => next.delete(version.key))
    loadingDownload.value = next
  }
}

const downloadVersion = async (version: ProjectFileControlVersion, showError = true) => {
  const summary = await downloadVersions([version])
  const success = !!summary && summary.failedCount === 0

  if (!success && showError) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Download Failed',
      text: summary?.results[0]?.error || 'Unable to download file version.',
    })
  }

  return success
}

const downloadSelectedHistoricalVersions = async () => {
  const versions = selectedHistoricalVersions.value
  if (!versions.length) return

  const summary = await downloadVersions(versions)
  const failedCount = summary?.failedCount ?? versions.length

  if (failedCount) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Download Failed',
      text: failedCount === versions.length
        ? 'Unable to download the selected file versions.'
        : `${failedCount} of ${versions.length} selected file versions could not be downloaded.`,
    })
  }
}

const toggleAllHistoricalVersions = (value: boolean | null) => {
  selectedHistoricalIds.value = value
    ? (payload.value?.historical_files ?? []).map(version => version.id)
    : []
}

const openConfirm = (version: ProjectFileControlVersion, action: 'make-main' | 'delete') => {
  if ((action === 'make-main' && !canMakeMain()) || (action === 'delete' && !canArchive())) {
    return
  }

  confirmVersion.value = version
  confirmAction.value = action
  showConfirmDialog.value = true
}

const getConfirmTitle = () => {
  return confirmAction.value === 'delete' ? 'Archive Current Version' : 'Make Current Version'
}

const getConfirmText = () => {
  return confirmAction.value === 'delete'
    ? 'Are you sure you want to move this main file to Previous Versions?'
    : 'Are you sure you want to make this historical file the main version?'
}

const getConfirmSubmit = () => {
  return confirmAction.value === 'delete' ? 'Update' : 'Make Current'
}

const openWaitingRoomDuplicateComparison = () => {
  showWaitingRoomDuplicatePrompt.value = false
  showWaitingRoomDuplicateComparison.value = true
}

const clearWaitingRoomDuplicateFlow = () => {
  showWaitingRoomDuplicatePrompt.value = false
  showWaitingRoomDuplicateComparison.value = false
  waitingRoomDuplicateFile.value = null
  waitingRoomDuplicateContext.value = null
}

const confirmWaitingRoomDuplicateComparison = async (comparisonPayload: DuplicateComparisonConfirmPayload) => {
  if (!waitingRoomDuplicateFile.value) return
  if (!waitingRoomDuplicateContext.value?.archive_replacement_file_id) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Waiting Room',
      text: 'Archive replacement context is missing.',
    })
    return
  }

  waitingRoomDuplicateApprovalLoading.value = true
  try {
    await $acceptWaitingRoomFile(
      comparisonPayload.waitingRoomFileId,
      {
        duplicate_resolution: 'waiting_room_file',
        reject_waiting_room_file_ids: comparisonPayload.rejectWaitingRoomFileIds,
        skip_status_validation: true,
        archive_replacement_file_id: waitingRoomDuplicateContext.value.archive_replacement_file_id,
      },
      { timeout: WAITING_ROOM_ARCHIVE_APPROVE_TIMEOUT_MS },
    )

    await fetchControlPayload()
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Waiting Room',
      text: 'Waiting Room file fetched as the main version.',
    })
    clearWaitingRoomDuplicateFlow()
  } catch (error: any) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Waiting Room',
      text: error?.response?.data?.detail || error?.message || 'Unable to fetch Waiting Room file.',
    })
  } finally {
    waitingRoomDuplicateApprovalLoading.value = false
  }
}

const executeConfirmedAction = async () => {
  if (!resolvedProjectId.value || !confirmVersion.value) {
    return
  }
  const action = confirmAction.value
  if ((action === 'make-main' && !canMakeMain()) || (action === 'delete' && !canArchive())) {
    showConfirmDialog.value = false
    confirmVersion.value = null
    return
  }

  const version = confirmVersion.value
  setActionLoading(action, version.id, true)

  try {
    const actionPayload = {
      file_id: version.id,
    }

    const response = action === 'delete'
      ? await $deleteProjectFileVersion(resolvedProjectId.value, actionPayload)
      : await $makeMainProjectFileVersion(resolvedProjectId.value, actionPayload)

    applyControlPayload(response.data.control)
    if (action === 'delete' && response.data.waiting_room_duplicate_file) {
      waitingRoomDuplicateFile.value = response.data.waiting_room_duplicate_file
      waitingRoomDuplicateContext.value = response.data.waiting_room_duplicate_context ?? null
      showWaitingRoomDuplicatePrompt.value = true
    }
    else if (action === 'delete') {
      clearWaitingRoomDuplicateFlow()
    }

    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: response?.data?.detail || 'File action completed successfully.',
    })
  } catch (error: any) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Action Failed',
      text: error?.response?.data?.detail || error?.message || 'Unable to complete this action.',
    })
  } finally {
    setActionLoading(action, version.id, false)
    showConfirmDialog.value = false
    confirmVersion.value = null
    confirmAction.value = 'make-main'
  }
}

const historyActionLabel = (action?: ProjectFileControlVersion['history_action']) => {
  if (!action) return '-'
  return action.charAt(0).toUpperCase() + action.slice(1)
}

const displayedHistoryAction = (version: ProjectFileControlVersion) => {
  return version.previous_name ? 'renamed' : version.history_action
}

const historyActionColor = (action?: ProjectFileControlVersion['history_action']) => {
  if (action === 'renamed') return 'info'
  if (action === 'archived') return 'secondary'
  return 'default'
}

watch(
  () => [props.modelValue, props.fileKey],
  ([isOpen]) => {
    if (isOpen) {
      linkedItemsAccordionModel.value = []
      fetchControlPayload()
    }
    else {
      resetState()
    }
  },
)
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="1400"
    @update:model-value="closeDialog"
  >
    <VCard>
      <VCardTitle class="d-flex align-center justify-space-between">
        <span>File Control</span>
        <VBtn icon="mdi-close" variant="text" size="small" @click="closeDialog" />
      </VCardTitle>
      <VDivider />

      <VCardText class="pa-2">
        <VProgressLinear v-if="loading" indeterminate class="mb-4" />

        <VAlert
          v-if="!loading && !payload && modelValue"
          type="info"
          variant="flat"
        >
          File control data is not available.
        </VAlert>

        <VRow v-else-if="payload" class="file-control-layout">
          <VCol cols="12" md="5">
            <VCard variant="outlined" class="mb-4">
              <VCardTitle class="text-subtitle-1 py-3">File Info</VCardTitle>
              <VDivider />
              <VCardText class="file-info">
                <div class="mb-2"><b>Name:</b> {{ payload.name || '-' }}</div>
                <div class="mb-2"><b>Extension:</b> {{ payload.extension || '-' }}</div>
                <div class="mb-2"><b>Upload Date:</b> {{ formatDate(payload.created) }} by {{ formatAuditUser(payload.created_by_display) }}</div>
                <div class="mb-2"><b>Updated Date:</b> {{ formatDate(payload.modified) }} by {{ formatAuditUser(payload.modified_by_display) }}</div>
              </VCardText>
            </VCard>

            <VExpansionPanels
              v-model="linkedItemsAccordionModel"
              multiple
              variant="accordion"
              class="mb-4 file-control-linked-items"
              gap="8"
            >
              <VExpansionPanel value="linked-items">
                <VExpansionPanelTitle class="mb-1 br-4">
                  <div class="d-flex align-center justify-space-between w-100 pr-2">
                    <span class="text-truncate">Linked Items</span>
                    <VChip
                      size="small"
                      variant="outlined"
                      color="grey-150"
                    >
                      {{ payload.linked_items.length }}
                      {{ payload.linked_items.length === 1 ? 'linked item' : 'linked items' }}
                    </VChip>
                  </div>
                </VExpansionPanelTitle>
                <VExpansionPanelText>
                  <VList v-if="payload.linked_items.length" density="compact" class="py-0">
                    <VListItem
                      v-for="item in payload.linked_items"
                      :key="`${item.type}-${item.id}`"
                      class="px-0"
                    >
                      <template #title>
                        <a :href="item.url" target="_blank" rel="noopener noreferrer" class="lable-linked-items-fm">
                          {{ item.type.toUpperCase() }}: {{ item.label }}
                        </a>
                      </template>
                    </VListItem>
                  </VList>
                  <div v-else class="text-caption text-medium-emphasis">
                    No linked items found.
                  </div>
                </VExpansionPanelText>
              </VExpansionPanel>
            </VExpansionPanels>

            <VCard variant="outlined" class="mb-4">
              <VCardTitle class="text-subtitle-1 py-3 d-flex justify-space-between">
                Current Version
                <div
                  v-if="payload.main_version"
                  class="d-flex gap-2"
                >
                  <VBtn
                    size="x-small"
                    :variant="selectedItemInPreview?.id === payload?.main_version?.id ? 'text' : 'flat'"
                    :loading="loadingPreview.has(payload.main_version.key)"
                    @click="previewVersion(payload.main_version)"
                    color='rgba(95, 73, 139, 1)'
                  >
                    Preview
                  </VBtn>
                  <VBtn
                    size="x-small"
                    :loading="isDownloadLoading(payload.main_version.key)"
                    @click="downloadVersion(payload.main_version)"
                  >
                    Download
                  </VBtn>
                  <VBtn
                    v-if="canArchive()"
                    size="x-small"
                    variant="outlined"
                    :loading="isDeleteLoading(payload.main_version.id)"
                    @click="openConfirm(payload.main_version, 'delete')"
                  >
                    Archive
                  </VBtn>
                </div>
              </VCardTitle>
              <VDivider />
              <VCardText
                    class="main-file-info"
                    :class=" { 'selected-item-file': selectedItemInPreview?.id === payload?.main_version?.id } "
              >
                <div
                  v-if="payload.main_version"
                  class="d-flex align-center justify-space-between gap-2"
                >
                  <div>
                    <div class="font-weight-medium">{{ payload.main_version.filename }}</div>
                    <div class="text-caption text-medium-emphasis">
                      <p class="mb-0">Upload Date: {{ formatDate(payload.main_version.created) }} by {{ formatAuditUser(payload.main_version.created_by_display) }}</p>
                      <p class="mb-0"></p>
                      <p v-if="payload.main_version.previous_name" class="mb-0">Previous Name: {{ payload.main_version.previous_name }}</p>
                      <p>Updated Date: {{ formatDate(payload.main_version.modified) }} by  {{ formatAuditUser(payload.main_version.modified_by_display) }}</p>
                    </div>
                  </div>
                 </div>
                <div v-else class="text-caption text-medium-emphasis red-darken-4">
                  No active main version.
                </div>
              </VCardText>
            </VCard>

            <VCard variant="outlined" class="mb-4">
              <VCardTitle class="d-flex align-center justify-space-between text-subtitle-1 py-3">
                <span>Previous Versions</span>
                <VBtn
                  size="x-small"
                  :disabled="!selectedHistoricalVersions.length"
                  :loading="isDownloadingSelectedHistoricalVersions"
                  @click="downloadSelectedHistoricalVersions"
                >
                  Download
                </VBtn>
              </VCardTitle>
              <VDivider />
              <VCardText class="pa-0 file-control-table-wrapper">
                <VTable density="compact" class="file-control-table">
                  <thead>
                    <tr>
                      <th class="px-2">
                        <VCheckboxBtn
                          :model-value="allHistoricalSelected"
                          :indeterminate="isHistoricalSelectionIndeterminate"
                          density="compact"
                          hide-details
                          @update:model-value="toggleAllHistoricalVersions"
                        />
                      </th>
                      <th>Upload date</th>
                      <th>Created by</th>
                      <th>Archive Date</th>
                      <th>Modified by</th>
                      <th>Previous Name</th>
                      <th>Action</th>
                      <th>Preview</th>
                      <th>Make Current</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in payload.historical_files"
                      :key="item.id"
                      :class=" { 'selected-item-file': selectedItemInPreview?.id === item.id } "
                    >
                      <td class="px-2">
                        <VCheckboxBtn
                          v-model="selectedHistoricalIds"
                          :value="item.id"
                          density="compact"
                          hide-details
                        />
                      </td>
                      <td>{{ formatDate(item.created) }}</td>
                      <td>{{ formatAuditUser(item.created_by_display) }}</td>
                      <td>{{ formatDate(item.modified) }}</td>
                      <td>{{ formatAuditUser(item.modified_by_display) }}</td>
                      <td>{{ item.previous_name || '-' }}</td>
                      <td>
                        <VChip
                          v-if="displayedHistoryAction(item)"
                          size="x-small"
                          :color="historyActionColor(displayedHistoryAction(item))"
                          label
                        >
                          {{ historyActionLabel(displayedHistoryAction(item)) }}
                        </VChip>
                        <span v-else>-</span>
                      </td>
                      <td>
                        <VBtn
                          size="x-small"
                          :variant="selectedItemInPreview?.id === item.id ? 'text' : 'flat'"
                          :loading="loadingPreview.has(item.key)"
                          @click="previewVersion(item)"
                          color='rgba(95, 73, 139, 1)'
                        >
                          Preview
                        </VBtn>
                      </td>
                      <td>
                        <VBtn
                          v-if="can('make_main', 'fmfilecontrol')"
                          size="x-small"
                          :variant="selectedItemInPreview?.id !== item.id ? 'text' : 'flat'"
                          color='success'
                          :loading="isMakeMainLoading(item.id)"
                          @click="openConfirm(item, 'make-main')"
                          :disabled="selectedItemInPreview?.id !== item.id"
                        >
                          Make Current
                        </VBtn>
                      </td>
                    </tr>
                    <tr v-if="!payload.historical_files.length">
                      <td colspan="8" class="text-center text-medium-emphasis py-3">No previous versions</td>
                    </tr>
                  </tbody>
                </VTable>
              </VCardText>
            </VCard>

          </VCol>

          <VCol cols="12" md="7">
            <VCard variant="outlined" class="h-100">
              <VCardTitle class="text-subtitle-1 py-3">Preview</VCardTitle>
              <VDivider />
              <VCardText class="pa-0">
                <div v-if="isPreviewLoading" class="file-control-preview-skeleton" aria-label="Loading preview">
                  <div class="file-control-preview-skeleton-toolbar">
                    <div class="file-control-preview-skeleton-block skeleton-line skeleton-line-title" />
                    <div class="file-control-preview-skeleton-block skeleton-line skeleton-line-action" />
                  </div>
                  <div class="file-control-preview-skeleton-page">
                    <div class="file-control-preview-skeleton-block skeleton-line skeleton-line-wide" />
                    <div class="file-control-preview-skeleton-block skeleton-line skeleton-line-medium" />
                    <div class="file-control-preview-skeleton-block skeleton-media" />
                    <div class="file-control-preview-skeleton-block skeleton-line skeleton-line-wide" />
                    <div class="file-control-preview-skeleton-block skeleton-line skeleton-line-short" />
                  </div>
                </div>
                <div v-else-if="previewUrl" class="file-control-preview-container">
                  <img
                    v-if="isImageFile(previewFilename)"
                    :src="previewUrl"
                    :alt="previewFilename"
                    class="file-control-preview-image"
                  />
                  <VuePdfEmbed
                    v-else-if="isPdfFile(previewFilename)"
                    :source="previewUrl"
                    class="file-control-preview-pdf"
                  />
                  <iframe
                    v-else-if="isTxtFile(previewFilename)"
                    :src="previewUrl"
                    :title="previewFilename"
                    class="file-control-preview-iframe"
                  />
                  <iframe
                    v-else
                    :src="previewUrl"
                    :title="previewFilename"
                    class="file-control-preview-iframe"
                  />
                </div>
                <div v-else class="text-medium-emphasis py-8 text-center">
                  Select a version preview from the table.
                </div>
              </VCardText>
            </VCard>
          </VCol>
        </VRow>
      </VCardText>

      <VCardActions>
        <VSpacer />
        <VBtn variant="text" @click="closeDialog">Close</VBtn>
      </VCardActions>
    </VCard>

    <ConfirmDialog
      v-model="showConfirmDialog"
      :title="getConfirmTitle()"
      :text="getConfirmText()"
      :submit-btn-name="getConfirmSubmit()"
      :cancel-btn-name="'Cancel'"
      @onSubmit="executeConfirmedAction"
    />

    <VDialog
      v-model="showWaitingRoomDuplicatePrompt"
      max-width="520"
    >
      <VCard>
        <VCardTitle class="text-subtitle-1">
          Waiting Room Duplicate Files
        </VCardTitle>
        <VDivider />
        <VCardText>
          <p class="mb-2">
            We found file in Waiting Room Duplicate Files.
          </p>
          <p class="mb-1">
            <b>File:</b> {{ waitingRoomDuplicateFile?.filename || '-' }}
          </p>
          <p class="mb-0">
            <b>Issue type:</b> {{ waitingRoomDuplicateFile?.issue_type_name || 'Duplicate Files' }}
          </p>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="clearWaitingRoomDuplicateFlow"
          >
            Cancel
          </VBtn>
          <VBtn
            color="primary"
            variant="flat"
            @click="openWaitingRoomDuplicateComparison"
          >
            Review
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <ProjectFilesWaitingRoomDuplicateComparisonDialog
      v-model="showWaitingRoomDuplicateComparison"
      :file="waitingRoomDuplicateFile"
      :loading="waitingRoomDuplicateApprovalLoading"
      duplicate-like-only
      waiting-room-only
      :archive-replacement-file-id="waitingRoomDuplicateContext?.archive_replacement_file_id"
      @cancel="clearWaitingRoomDuplicateFlow"
      @confirm="confirmWaitingRoomDuplicateComparison"
    />
  </VDialog>
</template>

<style scoped lang="scss">
.file-control-layout {
  min-height: 70vh;
}

.lable-linked-items-fm {
  text-decoration: underline;
  color: rgba(var(--v-theme-on-surface), 0.87);
  font-size: 0.875rem;
}
.file-control-table th,
.file-control-table td {
  white-space: nowrap;
  padding: 8px;
  font-size: 10px;
}

.file-control-preview-container {
  min-height: 60vh;
  max-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.file-control-preview-skeleton {
  min-height: 60vh;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
}

.file-control-preview-skeleton-toolbar,
.file-control-preview-skeleton-page {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 6px;
  background: rgba(var(--v-theme-surface), 1);
}

.file-control-preview-skeleton-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
}

.file-control-preview-skeleton-page {
  flex: 1;
  padding: 24px;
}

.file-control-preview-skeleton-block {
  position: relative;
  overflow: hidden;
  border-radius: 6px;
  background: rgba(var(--v-theme-on-surface), 0.08);
}

.file-control-preview-skeleton-block::after {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(var(--v-theme-on-surface), 0.08),
    transparent
  );
  animation: file-control-preview-skeleton 1.4s ease-in-out infinite;
  content: "";
  transform: translateX(-100%);
}

.skeleton-line {
  height: 14px;
  margin-block-end: 14px;
}

.skeleton-line-title {
  width: 42%;
  margin-block-end: 0;
}

.skeleton-line-action {
  width: 96px;
  height: 28px;
  margin-block-end: 0;
}

.skeleton-line-wide {
  width: 100%;
}

.skeleton-line-medium {
  width: 72%;
}

.skeleton-line-short {
  width: 46%;
}

.skeleton-media {
  width: 100%;
  height: 42vh;
  margin-block: 20px;
}

@keyframes file-control-preview-skeleton {
  100% {
    transform: translateX(100%);
  }
}

.file-control-preview-image {
  max-width: 100%;
  max-height: 68vh;
  object-fit: contain;
}

.file-control-preview-pdf {
  width: 100%;
  max-height: 68vh;
  overflow-y: auto;
}

.file-control-preview-iframe {
  width: 100%;
  height: 68vh;
  border: none;
}
.file-info{
  padding-block: 8px;
  font-size: 0.75rem;
}
.main-file-info {
  padding-block: 8px;
  p {
    margin-bottom: 0;
  }
}
.selected-item-file {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

:deep(.v-expansion-panel--active > .v-expansion-panel-title) {
  color: rgb(221, 8, 43);
  border-radius: 12px;
}

:deep(.v-expansion-panel--active > .v-expansion-panel-title .v-icon),
:deep(.v-expansion-panel--active > .v-expansion-panel-title .v-chip) {
  color: rgb(221, 8, 43);
}

.file-control-linked-items {
  :deep(.v-expansion-panel) {
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 6px  !important;
  }

  :deep(.v-expansion-panel--active) {
    border-radius: 14px !important;
  }
}
</style>
